import { startFlyToTopOfBox } from "../../lmv-compat/tools/FitToViewUtil";
import { DtViewerState } from "../DtViewerState";
import { RollTool2D, RollTool2DName } from "./RollTool2D";
import { av, THREE } from "@adsk/lmv";

/** Navigation actions allowed while 2D plan mode camera is locked. */
const PLAN_LOCK_SETTINGS = Object.freeze({
	orbit: false,
	pan: true,
	zoom: true,
	roll: true,
	fov: false,
	gotoview: false,
});

/**
 * LMV {@link Autodesk.Viewing.Viewer3D#activateDefaultNavigationTools} `is2d` argument:
 * For 2D, `is2d` should be true, and the default tool is "pan".
 * For 3D, `is2d` should be false, and the default tool is "orbit".
 * */
const LMV_NAV_IS_2D = true;
const LMV_NAV_IS_3D = false;

const CONTEXT_MENU_CALLBACK_ID = 'PlanModeCamera';

/**
 * Manages orthographic top-down camera lock for NoBIM 2D plan mode.
 * Saves the previous 3D camera and restores it when exiting back to 3D mode.
 */
export class PlanModeCamera {
	#contextMenuCallbackRegistered = false;
	#sectionLoadedEventRegistered = false;
	#boundOnContextMenuCallback;
	#boundOnSectionLoaded;
	#rollTool;

	constructor(facility) {
		this.facility = facility;
		this._active = false;
		/** Bumped on each enter/exit/viewer swap to cancel in-flight async applyPlanViewIfActive work. */
		this._enterGeneration = 0;
		this._savedCamera = null;
		this._prevNavLock = false;
		this._prevLockSettings = null;
		/** In-flight plan-mode camera fly: `stop` cancels the animation and resolves its Promise. */
		this._activeFly = null;

		this.#boundOnContextMenuCallback = this.#onContextMenuCallback.bind(this);
		this.#boundOnSectionLoaded = this.#onSectionLoaded.bind(this);
	}

	isActive() {
		return this._active;
	}

	#captureCamera(viewer) {
		const camera = viewer.getCamera();
		return {
			position: camera.position.clone(),
			target: camera.target.clone(),
			up: camera.up.clone(),
			isPerspective: camera.isPerspective,
			fov: camera.fov,
			worldup: camera.worldup?.clone?.() ?? new THREE.Vector3(0, 0, 1),
		};
	}

	#applySavedProjectionAndNav(viewer, savedCamera) {
		const sceneCamera = viewer.getCamera();
		if (savedCamera.fov !== undefined && sceneCamera.fov !== savedCamera.fov) {
			sceneCamera.setFov(savedCamera.fov);
		}
		if (savedCamera.isPerspective && !sceneCamera.isPerspective) {
			viewer.navigation.toPerspective();
		} else if (!savedCamera.isPerspective && sceneCamera.isPerspective) {
			viewer.navigation.toOrthographic();
		}
		if (savedCamera.worldup) {
			viewer.navigation.setWorldUpVector(savedCamera.worldup, false, true);
		}
		// Plan mode uses pan (2D nav); restoring to 3D mode always resets orbit regardless of projection.
		viewer.activateDefaultNavigationTools(LMV_NAV_IS_3D);
		viewer.impl.syncCamera();
	}

	#applyPlanProjection(viewer) {
		viewer.getExtension("Autodesk.BimWalk")?.deactivate();
		viewer.activateDefaultNavigationTools(LMV_NAV_IS_2D);
		viewer.navigation.toOrthographic();
	}

	#applyPlanLock(viewer) {
		viewer.setNavigationLock(true);
		viewer.setNavigationLockSettings(PLAN_LOCK_SETTINGS);
		viewer.toolController.activateTool(RollTool2DName);
		this.#rollTool.setUpAndFrontVectors(viewer.autocam.sceneUpDirection, viewer.autocam.sceneFrontDirection);
	}

	#onContextMenuCallback(menu) {
		if (!this._active) {
			return;
		}

		// Find and remove the section extension's entry.
		const i = menu.findIndex(item => item.title === 'Section');
		if (i !== -1) {
			menu.splice(i, 1);
		}
	}

	#onSectionLoaded() {
		const viewer = this.facility.viewer;
		viewer.registerContextMenuCallback(CONTEXT_MENU_CALLBACK_ID, this.#boundOnContextMenuCallback);
		this.#sectionLoadedEventRegistered = false;
	}

	#registerContextMenuCallback(viewer) {
		if (this.#contextMenuCallbackRegistered || this.#sectionLoadedEventRegistered) {
			return;
		}

		// We don't want the section entry to be available in the context menu while the plan mode camera is active.
		// So we register a context menu callback (invoked every time the menu is opened / build) that removes the
		// section entry from it (if present). Our callback needs to be registered after the section extension's
		// callback for this to work.

		const sectionExtLoaded = Boolean(viewer.getExtension('Autodesk.Tandem.Section'));
		if (sectionExtLoaded) {
			// If the section extension is loaded, register our context menu callback.
			viewer.registerContextMenuCallback(CONTEXT_MENU_CALLBACK_ID, this.#boundOnContextMenuCallback);
			this.#contextMenuCallbackRegistered = true;
		} else {
			// If the section extension is not (yet) loaded, register an event handler that listens to its activation
			// and registers our context menu callback in response.
			if (!this.#sectionLoadedEventRegistered) {
				viewer.addEventListener(av.EXTENSION_LOADED_EVENT, this.#boundOnSectionLoaded, { once: true });
				this.#sectionLoadedEventRegistered = true;
			}
		}
	}

	#unregisterContextMenuCallback(viewer) {
		if (this.#contextMenuCallbackRegistered) {
			viewer.unregisterContextMenuCallback(CONTEXT_MENU_CALLBACK_ID);
			this.#contextMenuCallbackRegistered = false;
		}

		if (this.#sectionLoadedEventRegistered) {
			viewer.removeEventListener(av.EXTENSION_LOADED_EVENT, this.#boundOnSectionLoaded);
			this.#sectionLoadedEventRegistered = false;
		}
	}

	/** Cancels the currently running fly and resolves its pending Promise (if any) */
	#cancelFly() {
		this.facility.viewAnimControl?.stop();
		this._activeFly?.stop();
		this._activeFly = null;
	}

	/** Starts and tracks an animated top-down camera fly to the given bounds. */
	#startFlyToBounds(viewer, box, durationSec) {
		if (!box || box.isEmpty()) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			this.#cancelFly();

			const control = startFlyToTopOfBox(viewer, box, durationSec, () => {
				this._activeFly = null;
				resolve();
			});

			this._activeFly = {
				stop: () => {
					control?.stop();
					resolve();
				},
			};
		});
	}

	/**
	 * Applies orthographic top-down projection and navigation lock when plan mode
	 * is active. (Does not snapshot the previous camera or lock settings.)
	 */
	async applyPlanViewIfActive() {
		if (!this._active) {
			return;
		}

		const viewer = this.facility.viewer;
		if (!viewer) {
			return;
		}

		const generation = this._enterGeneration;

		this.#applyPlanProjection(viewer);
		this.#applyPlanLock(viewer);

		try {
			await viewer.impl.waitForLoaderIdle();
		} catch (err) {
			console.warn("PlanModeCamera: waitForLoaderIdle failed", err);
		}

		// exit(), resetForViewerChange(), or a new enter() may run and bump _enterGeneration
		// while we await `waitForLoaderIdle`. Check if there was a newer plan-mode transition, and
		// cancel this call if so (e.g. exit then re-enter while waitForLoaderIdle was in flight).
		if (!this._active || generation !== this._enterGeneration) {
			return;
		}

		const bounds = viewer.impl.getVisibleBounds(false, false);
		await this.#startFlyToBounds(viewer, bounds, 0.5);
	}

	/**
	 * Animates the plan-mode camera to frame a level's world-space bounds.
	 * Same top-down fly as {@link applyPlanViewIfActive}, but for explicit bounds.
	 */
	flyToBounds(box, durationSec = 1) {
		if (!this._active) {
			return Promise.resolve();
		}

		const viewer = this.facility.viewer;
		if (!viewer) {
			return Promise.resolve();
		}

		return this.#startFlyToBounds(viewer, box, durationSec);
	}

	/**
	 * Clears plan mode state when the facility is bound to a different viewer instance.
	 * Unlocks navigation on the outgoing viewer without restoring the saved 3D camera.
	 *
	 * @param {object} [outgoingViewer] - The viewer being replaced, if any.
	 */
	resetForViewerChange(outgoingViewer) {
		// Invalidate any in-flight applyPlanViewIfActive on the outgoing viewer.
		this._enterGeneration++;

		if (!this._active) {
			return;
		}

		this._active = false;
		this.#cancelFly();

		if (outgoingViewer) {
			outgoingViewer.setNavigationLockSettings(this._prevLockSettings ?? {});
			outgoingViewer.setNavigationLock(this._prevNavLock);
			outgoingViewer.toolController.deactivateTool(RollTool2DName);
			this.#unregisterContextMenuCallback(outgoingViewer);
		}

		this._prevNavLock = false;
		this._prevLockSettings = null;
		this._savedCamera = null;
	}

	async enter() {
		if (this._active) {
			return;
		}

		const viewer = this.facility.viewer;
		if (!viewer) {
			return;
		}

		const toolController = viewer.toolController;
		this.#rollTool = toolController.getTool(RollTool2DName);
		if (!this.#rollTool) {
			this.#rollTool = new RollTool2D(viewer);
			toolController.registerTool(this.#rollTool);
		}

		this._active = true;
		// New enter session — invalidates async work from a prior enter still awaiting load/fly.
		this._enterGeneration++;

		this.#cancelFly();

		if (!this._savedCamera) {
			this._savedCamera = this.#captureCamera(viewer);
		}

		this._prevNavLock = viewer.getNavigationLock();
		this._prevLockSettings = viewer.getNavigationLockSettings();

		this.#registerContextMenuCallback(viewer);

		await this.applyPlanViewIfActive();
	}

	exit(restoreCamera = false) {
		const viewer = this.facility.viewer;
		if (!viewer) {
			return;
		}

		if (this._active) {
			this._active = false;

			this.#cancelFly();

			viewer.setNavigationLockSettings(this._prevLockSettings ?? {});
			viewer.setNavigationLock(this._prevNavLock);
			viewer.toolController.deactivateTool(RollTool2DName);

			this.#unregisterContextMenuCallback(viewer);
		}

		if (restoreCamera && this._savedCamera) {
			const savedCamera = this._savedCamera;
			this._savedCamera = null;

			this.#applySavedProjectionAndNav(viewer, savedCamera);
			DtViewerState.flyToView(this.facility, { camera: savedCamera }, 0.5, false);
		}
	}
}
